import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useCallback, useEffect, useMemo } from "react";

import {
  NEEDS_REVIEW_COUNT_FILTER_IDS,
  ShowFilterId,
  SHOW_FILTERS,
  applyShowFilterToSearch,
  detectActiveShowFilter,
  formatShowFilterLabel,
  migrateLegacyShowFilterTerms,
  visibleShowFilterIds,
} from "./manifestShowFilters";
import { mergeDefaultAndOmnisearch } from "./mergeDefaultAndOmnisearch";
import { useShowFilterCounts } from "./useShowFilterCounts";

interface ManifestShowFilterPickerViewProps {
  defaultSearch: string;
  /** The active omnisearch string, and the setter that replaces it. */
  omnisearch: string;
  onApply: (nextOmnisearch: string) => void;
  /**
   * True on the merged Shipment Documents list, which holds bills of lading as
   * well as manifests.
   *
   * Changes two things about the unfiltered option, and nothing else. Its label
   * stops saying "manifests", because the page around it says Shipment
   * Documents. And it stops showing a count, because every count here comes
   * from `PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY`: correct for the
   * manifest-only filters below it, wrong for a bucket that also holds
   * documents, and visibly wrong the moment a bill of lading is saved and the
   * row appears while the number does not move.
   */
  merged?: boolean;
  /**
   * Scope to count the needs-review preset against, when it differs from the
   * page scope. That preset is deliberately not bound to the year picker, so
   * counting it with the others would print a number here that disagrees with
   * both the prompt beside the page heading and the list its own click produces.
   */
  reviewCountSearch?: string;
}

/**
 * The picker itself, with no opinion about where omnisearch lives. V2's grid
 * keeps its omnisearch in `OmnisearchDataContext` and the merged V3 grid keeps
 * its own in the omnisearch-v2 URL store, so the state is passed in rather than
 * read from a context only one of the two screens has.
 */
function ManifestShowFilterPickerView({
  defaultSearch,
  omnisearch,
  onApply,
  merged = false,
  reviewCountSearch,
}: ManifestShowFilterPickerViewProps) {
  const { countByFilterId, loading } = useShowFilterCounts(defaultSearch);
  // Counts only the needs-review preset, against the year-free review scope.
  const { countByFilterId: reviewCountByFilterId } = useShowFilterCounts(
    reviewCountSearch ?? defaultSearch,
    NEEDS_REVIEW_COUNT_FILTER_IDS
  );

  const { featureFlags } = useFeatureFlags();
  const options = useMemo(
    () => visibleShowFilterIds(!!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies]),
    [featureFlags]
  );

  const mergedSearch = useMemo(
    () => mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
    [defaultSearch, omnisearch]
  );

  const activeFilterId = useMemo(() => {
    const detected = detectActiveShowFilter(mergedSearch);
    if (options.includes(detected)) return detected;
    // A saved URL can still name a preset this tenant is not offered — the EPA
    // correction preset on a tenant whose flag is off. Autocomplete renders the
    // input blank and warns when handed a value outside its options, so fall
    // back.
    //
    // The grid stays filtered: the omnisearch handler for the term is still
    // registered, so the dropdown reads "All manifests" over a narrowed grid,
    // and picking "All manifests" fires no change (MUI returns early when the
    // chosen option already equals the value). The term is visible in the
    // search box beside this picker and its clear button removes it, which is
    // the way out. Showing an option the tenant cannot use would be worse.
    return "allManifests";
  }, [mergedSearch, options]);

  const getOptionLabel = useCallback(
    (option: ShowFilterId) => {
      const def = SHOW_FILTERS[option];
      const unfilteredOnMergedList = merged && option === "allManifests";
      const label = unfilteredOnMergedList ? "All documents" : def.label;
      const count =
        option === "needsDocumentReview"
          ? reviewCountByFilterId.get(option)
          : countByFilterId.get(option);
      if (unfilteredOnMergedList || (loading && count == null)) {
        return label;
      }
      return formatShowFilterLabel(label, count ?? 0);
    },
    [countByFilterId, reviewCountByFilterId, loading, merged]
  );

  const handleChange = useCallback(
    (_: unknown, value: ShowFilterId) => {
      onApply(applyShowFilterToSearch(omnisearch, value));
    },
    [omnisearch, onApply]
  );

  return (
    <Autocomplete<ShowFilterId, false, true, false>
      size="small"
      options={options}
      value={activeFilterId}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        const { key, ...liProps } = props;
        return (
          <Tooltip
            key={key}
            title={SHOW_FILTERS[option].description}
            placement="right"
            enterDelay={300}
          >
            <li {...liProps}>{getOptionLabel(option)}</li>
          </Tooltip>
        );
      }}
      renderInput={(params) => <TextField {...params} label="Show" variant="standard" />}
      disableClearable
      sx={{ minWidth: 240 }}
    />
  );
}

export interface ManifestShowFilterPickerProps {
  defaultSearch: string;
}

/**
 * Rewrite a retired preset term in `omnisearch` to the one its preset emits
 * now, once, when a saved URL carrying it loads.
 *
 * Detecting the old term is not enough. Its omnisearch handler is still
 * registered, so the grid stays filtered by the old count column — which totals
 * every discrepancy kind, including the staff-only ones the preset now
 * excludes. Left unrewritten, the dropdown names the corrected preset and shows
 * its corrected count above rows the old term selected, and the reader cannot
 * fix it: choosing an option that already reads as active fires no change.
 *
 * Each caller owns its own omnisearch store, so the rewrite lives with the
 * owner rather than being pushed up from the picker.
 */
function useMigrateLegacyTerms(omnisearch: string, apply: (next: string) => void) {
  useEffect(() => {
    const migrated = migrateLegacyShowFilterTerms(omnisearch);
    // Not derived state flowing upward: this normalizes a URL the caller
    // already owns, and the preset vocabulary it needs lives here.
    // Self-limiting — the rewritten string carries no legacy term, so the next
    // run is a no-op. `no-pass-data-to-parent` warns here; its suggested shape
    // would copy the preset vocabulary into both callers.
    if (migrated !== omnisearch) apply(migrated);
  }, [omnisearch, apply]);
}

/**
 * Mutually exclusive Show filter dropdown (Year picker styling). Counts reflect
 * the page scope (tenant + year) only; choosing a filter updates omnisearch.
 */
export function ManifestShowFilterPicker({ defaultSearch }: ManifestShowFilterPickerProps) {
  const { omnisearch, setOmnisearchImmediate } = useOmnisearch();
  useMigrateLegacyTerms(omnisearch, setOmnisearchImmediate);

  return (
    <ManifestShowFilterPickerView
      defaultSearch={defaultSearch}
      omnisearch={omnisearch}
      onApply={setOmnisearchImmediate}
    />
  );
}

/**
 * The same picker for the merged Manifests list, reading the omnisearch-v2 URL
 * store. Same presets: the merged reader answers every one of them.
 */
export function MergedManifestShowFilterPicker({
  defaultSearch,
  reviewCountSearch,
}: ManifestShowFilterPickerProps & { reviewCountSearch?: string }) {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  useMigrateLegacyTerms(omnisearch, setOmnisearchImmediate);

  return (
    <ManifestShowFilterPickerView
      defaultSearch={defaultSearch}
      omnisearch={omnisearch}
      onApply={setOmnisearchImmediate}
      reviewCountSearch={reviewCountSearch}
      merged
    />
  );
}
